import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Flame, TrendingUp, Dumbbell, Heart } from "lucide-react";
import { PATHS, SERVICES, LOGO, LINKS, TESTIMONIALS } from "../lib/config";
import CalendlyModal from "../components/CalendlyModal";
import Nav from "../components/Nav";

export default function PathPage() {
    const { slug } = useParams();
    const path = PATHS[slug];
    const [calOpen, setCalOpen] = useState(false);
    const onBook = () => setCalOpen(true);

    useEffect(() => { window.scrollTo(0, 0); }, [slug]);

    if (!path) {
        return (
            <div className="min-h-screen bg-powder flex flex-col items-center justify-center gap-6 px-5 text-center">
                <h1 className="font-serif text-4xl">Percorso non trovato</h1>
                <Link to="/" className="btn-fuchsia" data-testid="path-back-home">Torna alla home</Link>
            </div>
        );
    }

    const isFitmama = path.slug === "fitmama";
    // Path color: fuchsia for FitMama, arancio (accent) for General Population
    const pc = isFitmama
        ? {
              text: "text-fuchsia",
              bg: "bg-fuchsia",
              bgSoft: "bg-fuchsia/10",
              borderHover: "hover:border-fuchsia/40",
              ringSoft: "bg-fuchsia/20",
          }
        : {
              text: "text-accent",
              bg: "bg-accent",
              bgSoft: "bg-accent/10",
              borderHover: "hover:border-accent/40",
              ringSoft: "bg-accent/20",
          };

    // Testimonial to show on this path
    const pathTestimonial = isFitmama
        ? TESTIMONIALS.find((t) => t.category === "Gravidanza")
        : TESTIMONIALS.find((t) => t.category === "Personal Training");

    // Note: PROGRAMS array split by category is no longer used; we now show 2 riquadri

    // Packages / pricing tiers (used only on General Population page)
    const packages = [
        {
            id: "base",
            name: "Inizia",
            weeks: 6,
            badge: null,
            subtitle: "Per iniziare e capire il metodo",
            price: "€ 150",
            features: [
                "Primo incontro conoscitivo",
                "Piano di allenamento personalizzato",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "Supporto WhatsApp",
            ],
        },
        {
            id: "plus",
            name: "Progredisci",
            weeks: 12,
            badge: "Più scelto",
            scarcity: "Solo 5 posti nuovi al mese",
            subtitle: "Il pacchetto più equilibrato",
            price: "€ 270",
            features: [
                "Primo incontro conoscitivo",
                "Piani di allenamento personalizzati (aggiornati a metà percorso)",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "Consulenza di monitoraggio a metà percorso",
                "Correzioni video",
                "Supporto WhatsApp",
            ],
        },
        {
            id: "pro",
            name: "Trasformati",
            weeks: 24,
            badge: null,
            subtitle: "Per una trasformazione reale",
            price: "€ 480",
            features: [
                "Primo incontro conoscitivo",
                "Piani di allenamento personalizzati (aggiornati ogni 6 settimane)",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "3 consulenze di monitoraggio",
                "Correzioni video",
                "Supporto WhatsApp",
            ],
        },
    ];

    const themeAccent = isFitmama ? "fuchsia" : "accent";
    const accentBadgeCls = isFitmama
        ? { bg: "bg-fuchsia", text: "text-fuchsia", border: "border-fuchsia", ring: "ring-fuchsia" }
        : { bg: "bg-accent", text: "text-accent", border: "border-accent", ring: "ring-accent" };

    const packagesSection = (
        <section className="relative bg-white py-24 md:py-32" id="pacchetti">
            <div className="max-w-[1400px] mx-auto px-5 md:px-12">
                {/* Small testimonial above packages */}
                {pathTestimonial && (
                    <motion.figure
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="max-w-2xl mx-auto mb-16 md:mb-20 text-center"
                        data-testid={`path-testimonial-${isFitmama ? "fitmama" : "general"}`}
                    >
                        <p className={`text-[10px] uppercase tracking-[0.28em] font-sans ${accentBadgeCls.text} mb-4`}>
                            Dicono di me
                        </p>
                        <blockquote className="font-serif italic text-xl md:text-2xl leading-snug text-ink mb-3">
                            &ldquo;{pathTestimonial.quote}&rdquo;
                        </blockquote>
                        <figcaption className="font-sans text-sm text-ink-muted">
                            — {pathTestimonial.name} · {pathTestimonial.role}
                        </figcaption>
                    </motion.figure>
                )}

                <div className="max-w-3xl mb-14 md:mb-20">
                    <p className="text-xs uppercase tracking-[0.28em] font-sans text-ink-muted mb-4">
                        <span className={`inline-block w-8 h-px ${accentBadgeCls.bg} align-middle mr-3`} />
                        Pacchetti e costi
                    </p>
                    <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] font-bold text-ink mb-6">
                        Scegli il tuo <span className={`italic ${accentBadgeCls.text}`}>pacchetto</span>.
                    </h2>
                    <p className="font-sans text-base md:text-lg text-ink-muted max-w-xl">
                        Tre percorsi con durata e supporto crescenti. Più a lungo ti alleni con me, più il percorso si adatta e cresce con te.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-5 md:gap-6 items-stretch">
                    {packages.map((p, i) => {
                        const featured = p.badge !== null;
                        return (
                            <motion.div
                                key={p.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                className={`relative flex flex-col text-center bg-powder p-6 md:p-8 border transition-colors duration-500 ${
                                    featured
                                        ? `${accentBadgeCls.border} border-2 shadow-2xl md:-mt-4`
                                        : "border-ink/10"
                                }`}
                                data-testid={`package-${p.id}`}
                            >
                                {featured && (
                                    <span
                                        className={`absolute -top-3 left-1/2 -translate-x-1/2 ${accentBadgeCls.bg} text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1 rounded-full whitespace-nowrap`}
                                    >
                                        ⭐ {p.badge}
                                    </span>
                                )}

                                <div className="mb-6">
                                    <span className="text-[10px] uppercase tracking-[0.28em] text-ink-muted block mb-2">
                                        {p.weeks} settimane
                                    </span>
                                    <h3 className="font-serif text-3xl md:text-4xl leading-none text-ink">
                                        {p.name}
                                    </h3>
                                    <p className="font-sans text-sm text-ink-muted mt-2">
                                        {p.subtitle}
                                    </p>
                                </div>

                                <div className={`mb-6 pb-6 border-b border-ink/10`}>
                                    {p.scarcity && (
                                        <p className={`text-[11px] uppercase tracking-[0.24em] font-sans font-medium ${accentBadgeCls.text} mb-3`}>
                                            {p.scarcity}
                                        </p>
                                    )}
                                    <span className={`font-serif text-4xl md:text-5xl font-bold ${accentBadgeCls.text}`}>
                                        {p.price}
                                    </span>
                                    <span className="font-sans text-sm text-ink-muted ml-2">
                                        / percorso
                                    </span>
                                </div>

                                <ul className="space-y-3 mb-8 flex-1 text-left">
                                    {p.features.map((f, j) => (
                                        <li key={j} className="flex items-start gap-3">
                                            <span
                                                className={`mt-0.5 w-5 h-5 rounded-full ${accentBadgeCls.bg} text-white flex items-center justify-center shrink-0`}
                                            >
                                                <Check size={12} />
                                            </span>
                                            <span className="font-sans text-sm text-ink">{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href={LINKS.whatsappStart}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`package-cta-${p.id}`}
                                    className={`${themeAccent === "accent" ? "btn-accent" : "btn-fuchsia"} justify-center !py-3 text-sm ${
                                        featured ? "" : "!bg-ink hover:!bg-ink/80"
                                    }`}
                                >
                                    Prenota ora il tuo pacchetto <ArrowUpRight size={16} />
                                </a>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="text-center mt-10 md:mt-14">
                    <p className="font-sans text-sm text-ink-muted mb-3">Hai dubbi o domande?</p>
                    <a
                        href={LINKS.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="packages-doubts-link"
                        className={`inline-flex items-center gap-2 ${themeAccent === "accent" ? "btn-accent" : "btn-fuchsia"} !py-2.5 !px-5 text-sm`}
                    >
                        Clicca qui per richiedere info <ArrowUpRight size={16} />
                    </a>
                </div>
            </div>
        </section>
    );

    return (
        <div className="min-h-screen bg-powder text-ink" data-testid="path-page">
            <div className="grain-overlay" />
            <Nav onBook={onBook} variant="solid" theme={isFitmama ? "fuchsia" : "accent"} />

            {/* Hero */}
            {isFitmama ? (
                <section className="relative bg-powder-darker overflow-hidden pt-24 pb-16 md:pt-28 md:pb-20">
                    <div className="max-w-[1000px] mx-auto px-5 flex flex-col items-center text-center">
                        <motion.img
                            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
                            src={LOGO} alt="FitMama"
                            className="w-52 h-52 md:w-80 md:h-80 rounded-full object-cover shadow-2xl ring-8 ring-white mb-8 md:mb-10"
                        />
                        <h1 className="font-serif text-5xl md:text-7xl font-bold leading-[0.95] text-ink">
                            <span className="text-fuchsia">#</span>FitMama
                        </h1>
                        <p className="font-serif italic text-xl md:text-2xl mt-4 text-fuchsia">{path.tagline}</p>
                    </div>
                </section>
            ) : (
                <section className="relative min-h-[55vh] md:min-h-[65vh] flex items-end overflow-hidden bg-ink">
                    <motion.img
                        initial={{ scale: 1.12 }} animate={{ scale: 1 }}
                        transition={{ duration: 1.5, ease: [0.65, 0, 0.35, 1] }}
                        src={path.image} alt={path.label}
                        className="absolute inset-0 w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />

                    <div className="relative z-10 px-5 md:px-12 pb-10 md:pb-14 max-w-[1400px] mx-auto w-full text-white">
                        <h1 className="font-serif text-5xl md:text-7xl font-bold leading-[0.95]">{path.label}</h1>
                        <p className="font-serif italic text-xl md:text-2xl mt-4 text-white/90">{path.tagline}</p>
                    </div>
                </section>
            )}

            {/* Body */}
            <section className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
                <motion.p
                    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="font-serif text-2xl md:text-3xl leading-snug mb-6"
                >
                    {path.intro}
                </motion.p>
                {path.highlight && (
                    <motion.p
                        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className={`font-serif italic text-xl md:text-2xl ${pc.text} leading-snug ${!isFitmama ? "mb-8" : "mb-14"}`}
                    >
                        {path.highlight}
                    </motion.p>
                )}
                {!isFitmama && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mb-14"
                    >
                        <button
                            onClick={onBook}
                            data-testid="path-book-btn-intro"
                            className="btn-ghost"
                        >
                            Prenota la tua consulenza gratuita <ArrowUpRight size={16} />
                        </button>
                    </motion.div>
                )}

                <h2 className="font-serif text-3xl md:text-4xl mb-6 text-left">A chi è rivolto</h2>
                <ul className="space-y-4 mb-4 text-left">
                    {path.forWho.map((item, i) => (
                        <li key={i} className="flex gap-4 items-start border-b border-ink/10 pb-4">
                            <span className={`mt-1 w-6 h-6 rounded-full ${pc.bg} text-white flex items-center justify-center shrink-0`}>
                                <Check size={14} />
                            </span>
                            <span className="font-sans text-base md:text-lg">{item}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* General Population: struttura del percorso + pacchetti */}
            {!isFitmama && (
                <>
                    {/* Struttura del percorso */}
                    <section className="relative bg-powder-darker py-20 md:py-28">
                        <div className="max-w-3xl mx-auto px-5 md:px-8">
                            <div className="bg-accent text-white p-7 md:p-9">
                                <p className="text-xs uppercase tracking-[0.28em] font-sans text-white/80 mb-6">
                                    Struttura del percorso
                                </p>
                                <ul className="space-y-4">
                                    {[
                                        "Primo incontro conoscitivo",
                                        "Creazione del programma su misura e accesso all'app",
                                        "Correzioni video ed eventuali modifiche in corso d'opera",
                                        "Attività di monitoring e follow-up",
                                    ].map((b, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <span className="mt-1 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                                                <Check size={12} />
                                            </span>
                                            <span className="font-sans text-sm md:text-base">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {packagesSection}
                </>
            )}

            {/* FitMama-only: 2 clean cards + shared benefits */}
            {isFitmama && (
                <>
                    <section className="relative bg-white py-24 md:py-32" id="riquadri-fitmama">
                        <div className="max-w-[1400px] mx-auto px-5 md:px-12">
                            <div className="max-w-3xl mb-14 md:mb-20">
                                <p className="text-xs uppercase tracking-[0.28em] font-sans text-ink-muted mb-4">
                                    <span className="inline-block w-8 h-px bg-fuchsia align-middle mr-3" />
                                    I percorsi possibili
                                </p>
                                <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] font-bold text-ink mb-6">
                                    Due momenti,<br />
                                    <span className="italic text-fuchsia">un percorso su misura.</span>
                                </h2>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                                {[
                                    {
                                        num: "01",
                                        title: "Allenamento in Gravidanza",
                                        img: SERVICES.find((s) => s.category === "gravidanza")?.img,
                                        desc: "Raccontami a che mese sei, come stai e che obiettivo hai. Costruiremo insieme un percorso sicuro e adatto al tuo trimestre, che ti accompagni fino al parto e che si adatti alle tue esigenze.",
                                        id: "gravidanza",
                                    },
                                    {
                                        num: "02",
                                        title: "Allenamento Post Gravidanza",
                                        img: SERVICES.find((s) => s.category === "post-parto")?.img,
                                        desc: "Il tuo corpo ha appena creato una vita. Insieme lo ricostruiamo con cura e con i tuoi tempi: dal recupero del pavimento pelvico al ritorno alla forma, con obiettivi realistici e allenamenti pensati per la neo-mamma.",
                                        id: "post-parto",
                                    },
                                ].map((r, i) => (
                                    <motion.div
                                        key={r.num}
                                        id={r.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-60px" }}
                                        transition={{ duration: 0.7, delay: i * 0.1 }}
                                        className="group bg-powder p-6 md:p-8 border border-ink/5 hover:border-fuchsia/40 transition-colors duration-500 flex flex-col"
                                        data-testid={`fitmama-card-${r.id}`}
                                    >
                                        <div className="flex items-center gap-5 mb-6">
                                            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden ring-4 ring-white shadow-xl shrink-0">
                                                <img src={r.img} alt={r.title} className="w-full h-full object-cover" />
                                            </div>
                                            <h3 className="font-serif text-2xl md:text-3xl leading-tight text-ink">
                                                {r.title}
                                            </h3>
                                        </div>
                                        <p className="font-sans text-sm md:text-base text-ink-muted leading-relaxed mb-8 flex-1">
                                            {r.desc}
                                        </p>
                                        <Link
                                            to={`/fase/${r.id}`}
                                            data-testid={`fitmama-cta-${r.id}`}
                                            className="btn-fuchsia self-start !py-2.5 !px-5 text-sm"
                                        >
                                            Scopri di più <ArrowUpRight size={16} />
                                        </Link>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Shared benefits — struttura del percorso */}
                    <section className="relative bg-powder-darker py-20 md:py-28">
                        <div className="max-w-3xl mx-auto px-5 md:px-8">
                            <div className="bg-fuchsia text-white p-7 md:p-9">
                                <p className="text-xs uppercase tracking-[0.28em] font-sans text-white/80 mb-6">
                                    Cosa include ogni programma
                                </p>
                                <ul className="space-y-4">
                                    {[
                                        "Adatto a principianti ed esperti",
                                        "Scegli la durata del tuo programma: 6, 12 o 24 settimane",
                                        "Video con la spiegazione di ogni esercizio",
                                        "Calendario settimanale di allenamento",
                                        "Progressione graduale e sicura",
                                        "Supporto WhatsApp per ogni dubbio",
                                    ].map((b, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <span className="mt-1 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                                                <Check size={12} />
                                            </span>
                                            <span className="font-sans text-sm md:text-base">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>
                </>
            )}

            <CalendlyModal open={calOpen} onOpenChange={setCalOpen} />
        </div>
    );
}
